import jsPDF from "jspdf";
import { imgData as smestLogo } from "../../Helpers/smestLogoBase64ImgData";

type WatermarkOpts = {
  /** 0..1 */
  opacity?: number;
  /** Portion of page diagonal to use for watermark width (0..1) */
  widthFactor?: number;
  /** Degrees, default -45 */
  angleDeg?: number;
  /** Optional manual nudge (in PDF units) if your build behaves slightly differently */
  yAdjust?: number;
  xAdjust?: number;
};

/**
 * Centered diagonal watermark using addImage's rotation parameter.
 * This version adjusts the Y offset for jsPDF's screen-space coordinate system (Y grows downward).
 */
export function drawSmestWatermarkOnCurrentPage(
  doc: jsPDF,
  opts: WatermarkOpts = {}
) {
  const angleDeg = typeof opts.angleDeg === "number" ? opts.angleDeg : -45;
  const opacity = typeof opts.opacity === "number" ? opts.opacity : 0.06;
  const widthFactor =
    typeof opts.widthFactor === "number" ? opts.widthFactor : 0.85;
  const xAdjust = opts.xAdjust ?? 0;
  const yAdjust = opts.yAdjust ?? 0;

  const pageSize: any = (doc as any).internal?.pageSize;
  const pageWidth =
    typeof pageSize?.getWidth === "function"
      ? pageSize.getWidth()
      : pageSize?.width ?? 210;
  const pageHeight =
    typeof pageSize?.getHeight === "function"
      ? pageSize.getHeight()
      : pageSize?.height ?? 297;

  // Image ratio (fallback if getImageProperties is not available)
  let ratio = 4; // safe wide-logo default
  try {
    const p = (doc as any).getImageProperties?.(smestLogo as any);
    if (p && p.width && p.height) ratio = p.width / p.height;
  } catch {}

  const diagonal = Math.sqrt(pageWidth * pageWidth + pageHeight * pageHeight);
  const wmWidth = diagonal * widthFactor;
  const wmHeight = wmWidth / ratio;

  const cx = pageWidth / 2 + xAdjust;
  const cy = pageHeight / 2 + yAdjust;

  const theta = (angleDeg * Math.PI) / 180;
  const c = Math.cos(theta);
  const s = Math.sin(theta);
  const halfW = wmWidth / 2;
  const halfH = wmHeight / 2;

  // For jsPDF (y down), rotate vector (halfW, halfH) by theta
  // using screen-space rotation: x' = x*c - y*s, y' = -x*s + y*c
  const offsetX = halfW * c - halfH * s;
  const offsetY = -halfW * s + halfH * c;

  const xTL = cx - offsetX;
  const yTL = cy - offsetY;

  const draw = () =>
    (doc as any).addImage(
      smestLogo as any,
      "PNG",
      xTL,
      yTL,
      wmWidth,
      wmHeight,
      undefined,
      "FAST",
      angleDeg
    );

  try {
    const hasG = (doc as any).GState && (doc as any).setGState;
    if (hasG) {
      const g = new (doc as any).GState({ opacity });
      (doc as any).saveGraphicsState?.();
      (doc as any).setGState?.(g);
      draw();
      (doc as any).restoreGraphicsState?.();
      return;
    }
  } catch {}

  draw();
}

export function addSmestWatermarkToAllPages(
  doc: jsPDF,
  opts: WatermarkOpts = {}
) {
  const total = (doc as any).getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    (doc as any).setPage(i);
    drawSmestWatermarkOnCurrentPage(doc, opts);
  }
}
